import lazyModule from '../../utils/lazyModule';
import { hasPermission } from '../../utils/permissions';

/**
 * Every page of the Admin Panel, in one list. The rail, the routes, the overview and search are
 * all built from it, so adding a page is one entry here plus its component.
 *
 * - `path` is relative to /admin (so `users` is /admin/users).
 * - `group` files the page in the rail; `queue` is the one daily-work page that sits above the
 *   groups rather than among the settings.
 * - `permission` is the box that opens it: the same one the server checks for that page's data
 *   (Positions `admin.role_permissions`, Company Directory pages `dossier.manage_catalogs`, Statistics
 *   targets `stats.edit_targets`, Email reports `phm.admin_recipients`, Alerts `phm.alert_settings`).
 *   `alsoOpenedBy` adds `admin.panel` for the pages it always opened. A person holding only one of these
 *   boxes is let into the panel and sees just their pages (`canOpenAdmin`); the pages left on
 *   `admin.panel` alone stay with the Admin panel. Users is one of those on live: its list route
 *   (`GET /api/users`) asks for the panel, `phm.access` or `schedule.edit`, not `admin.users`.
 * - `keywords` are what people call the thing when they look for it, so a search for "grace",
 *   "TL" or "onboarding" finds the page even though the word is not in its title.
 */
export const ADMIN_GROUPS = [
  { id: 'queue', label: '' },
  { id: 'people', label: 'People and access' },
  { id: 'directory', label: 'Company Directory' },
  { id: 'reporting', label: 'Reporting and alerts' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'workspace', label: 'Workspace' }
];

export const ADMIN_PAGES = [
  {
    id: 'schedule-notes',
    path: 'schedule-notes',
    group: 'queue',
    title: 'Schedule note review',
    description: 'Confirm roster note schedule changes and overtime flags.',
    icon: 'queue',
    permission: 'admin.panel',
    keywords: ['roster note', 'overtime', 'approve', 'dismiss', 'shift hours', 'sheet note', 'review queue'],
    Component: lazyModule(() => import('./pages/ScheduleNotesPage'))
  },
  {
    id: 'parking-requests',
    path: 'parking-requests',
    group: 'queue',
    title: 'Parking requests',
    description: 'Car plate changes and extra plates that people with parking have asked for.',
    icon: 'checklist',
    permission: 'admin.panel',
    keywords: ['parking', 'car', 'car plate', 'plate', 'plates', 'licence plate', 'registration', 'vehicle', 'approve', 'reject', 'request', 'change a plate', 'add a plate'],
    Component: lazyModule(() => import('./pages/PlateRequestsPage'))
  },
  {
    id: 'users',
    path: 'users',
    group: 'people',
    title: 'Users',
    description: 'Add, edit and remove user accounts, and set their position, shift and team.',
    icon: 'users',
    permission: 'admin.panel',
    keywords: ['accounts', 'logins', 'add user', 'delete user', 'bulk', 'shift lead', 'micro team', 'team', 'email', 'nickname'],
    Component: lazyModule(() => import('./pages/UsersPage'))
  },
  {
    id: 'positions',
    path: 'positions',
    group: 'people',
    title: 'Positions and permissions',
    description: 'Control what each position can open and change, and manage the positions themselves.',
    icon: 'lock',
    permission: 'admin.role_permissions',
    alsoOpenedBy: 'admin.panel',
    keywords: ['roles', 'access', 'matrix', 'rbac', 'grants', 'rename position', 'delete position', 'add position', 'break scheduler roster'],
    Component: lazyModule(() => import('./pages/PositionsPage'))
  },
  {
    id: 'structure',
    path: 'structure',
    group: 'directory',
    title: 'Structure',
    description: 'Departments, teams, positions and the org tree, and the employee statuses.',
    icon: 'structure',
    permission: 'dossier.manage_catalogs',
    keywords: ['departments', 'teams', 'org tree', 'company chart', 'statuses', 'employee status', 'catalogs', 'reporting line'],
    Component: lazyModule(() => import('./pages/CatalogPages').then((m) => ({ default: m.StructurePage })))
  },
  {
    id: 'pay-benefits',
    path: 'pay-benefits',
    group: 'directory',
    title: 'Pay and benefits',
    description: 'The insurance base cap, the benefits every profile lists, and the bonus types.',
    icon: 'coin',
    permission: 'dossier.manage_catalogs',
    keywords: ['insurance', 'cap', 'payroll', 'sign-off', 'benefits', 'bonus', 'bonuses', 'perks', 'catalogs'],
    Component: lazyModule(() => import('./pages/CatalogPages').then((m) => ({ default: m.PayBenefitsPage })))
  },
  {
    id: 'checklists',
    path: 'checklists',
    group: 'directory',
    title: 'Checklists',
    description: 'The onboarding and offboarding steps shown on every profile.',
    icon: 'checklist',
    permission: 'dossier.manage_catalogs',
    keywords: ['onboarding', 'offboarding', 'joiner', 'leaver', 'tasks', 'steps', 'catalogs'],
    Component: lazyModule(() => import('./pages/CatalogPages').then((m) => ({ default: m.ChecklistsPage })))
  },
  {
    id: 'email-reports',
    path: 'email-reports',
    group: 'reporting',
    title: 'Email reports',
    description: 'Who receives the daily Productivity emails.',
    icon: 'mail',
    permission: 'phm.admin_recipients',
    alsoOpenedBy: 'admin.panel',
    keywords: ['recipients', 'daily', 'productivity', 'phm', 'tl', 'team lead', 'sl', 'shift lead', 'head of cs', 'emails'],
    Component: lazyModule(() => import('./pages/EmailReportsPage'))
  },
  {
    id: 'alerts',
    path: 'alerts',
    group: 'reporting',
    title: 'Alerts',
    description: 'Slack alerts for missed, late and long breaks, and the email for very long ones.',
    icon: 'bell',
    permission: 'phm.alert_settings',
    alsoOpenedBy: 'admin.panel',
    keywords: ['slack', 'break', 'grace', 'threshold', 'timing', 'missed slot', 'not started', 'over duration', '2h', 'notifications'],
    Component: lazyModule(() => import('./pages/AlertsPage'))
  },
  {
    id: 'report-tools',
    path: 'report-tools',
    group: 'reporting',
    title: 'Report tools',
    description: 'Send a test, resend a corrected report, or flag a day whose data is wrong.',
    icon: 'send',
    permission: 'admin.panel',
    keywords: ['test report', 'monthly', 'resend', 'corrected', 'bad day', 'flag', 'block', 'placeholder', 'plain text'],
    Component: lazyModule(() => import('./pages/ReportToolsPage'))
  },
  {
    id: 'quality-rules',
    path: 'quality-rules',
    group: 'reporting',
    title: 'Quality report rules',
    description: 'The pass line, the 1x1 lines, the planned sessions and the working pack thresholds the Quality reports use.',
    icon: 'checklist',
    permission: 'admin.panel',
    keywords: ['quality', 'qa', 'pass line', 'pass score', '90', '80', 'candidate', 'top performer', '1x1', 'one to one', 'sessions', 'weekly', 'monthly', 'biweekly', 'threshold', 'declining', 'improvement', 'positive progress', 'insufficient sample', 'reviews', 'scored tickets', 'working pack', 'audit mistake rate', 'thresholds'],
    Component: lazyModule(() => import('./pages/QualityRulesPage'))
  },
  {
    id: 'google-sheets',
    path: 'google-sheets',
    group: 'integrations',
    title: 'Google Sheets',
    description: 'Import roster, schedules and “at work” status from the GrafiX sheet.',
    icon: 'table',
    permission: 'admin.panel',
    keywords: ['sync', 'spreadsheet', 'grafix', 'schedule', 'roster', 'import', 'quarter', 'sheet tab', 'at work'],
    Component: lazyModule(() => import('./pages/GoogleSheetsPage'))
  },
  {
    id: 'google-calendar',
    path: 'google-calendar',
    group: 'integrations',
    title: 'Google Calendar',
    description: 'The shared Google account that leads share their calendars with.',
    icon: 'calendar',
    permission: 'admin.panel',
    keywords: ['meetings', 'connect', 'oauth', 'shared account', 'calendar', 'google account', 'disconnect'],
    Component: lazyModule(() => import('./pages/GoogleCalendarPage'))
  },
  {
    id: 'home-tools',
    path: 'home-tools',
    group: 'workspace',
    title: 'Home page tools',
    description: 'Home-page links, groups and icons.',
    icon: 'link',
    permission: 'admin.panel',
    keywords: ['external tools', 'links', 'icons', 'e-ops home', 'tiles', 'launcher', 'credentials'],
    Component: lazyModule(() => import('./pages/HomeToolsPage'))
  },
  {
    id: 'desks',
    path: 'desks',
    group: 'workspace',
    title: 'Desks',
    description: 'The Break Manager\'s floor plan: add desks, change numbers, lock a desk and choose who may take it.',
    icon: 'desk',
    permission: 'breaks.manage_desks',
    keywords: ['desk', 'desks', 'floor plan', 'office', 'seat', 'seats', 'locked', 'reserved', 'personal', 'management', 'assign', 'break manager', 'desk or home', 'number'],
    Component: lazyModule(() => import('./pages/DesksPage'))
  },
  {
    id: 'stats-targets',
    path: 'stats-targets',
    group: 'workspace',
    title: 'Insights targets',
    description: 'The minimum and bonus requirements, Agent of the Month and the quarterly bonus, month by month.',
    icon: 'target',
    // Statistics targets have their own box (Set Statistics targets, Admin until it is ticked), so Head of CS and Zendesk Administrator,
    // who hold every key, see the page and nobody else does.
    permission: 'stats.edit_targets',
    keywords: ['kpi', 'minimum', 'bonus', 'requirements', 'targets', 'monthly', 'agent of the month', 'employee of the month', 'quarterly', 'green', 'yellow', 'thresholds', 'qa avg', 'sat', 'colours', 'colors', 'goals', 'statistics'],
    Component: lazyModule(() => import('./pages/StatsTargetsPage'))
  },
  {
    id: 'stats-agent-roles',
    path: 'stats-agent-roles',
    group: 'workspace',
    title: 'Who counts in Statistics',
    description: 'The Zendesk roles whose people are in Statistics, the team averages and the quarterly team bonus.',
    icon: 'users',
    permission: 'stats.edit_targets',
    keywords: ['statistics', 'insights', 'counted', 'who counts', 'zendesk role', 'roles', 'cs agent', 'shift lead', 'senior', 'agents', 'team average', 'stats_agent_role_name', 'environment', 'picker', 'members'],
    Component: lazyModule(() => import('./pages/StatsAgentRolesPage'))
  }
];

export const adminPagePath = (page) => `/admin/${page.path}`;

/** Whether this user may open one page: its own box, or the Admin panel box where the page says that opens it too. */
export const canOpenAdminPage = (user, page) => hasPermission(user, page.permission)
  || Boolean(page.alsoOpenedBy && hasPermission(user, page.alsoOpenedBy));

/** The pages this user may open, in rail order. */
export const visibleAdminPages = (user) => ADMIN_PAGES.filter((page) => canOpenAdminPage(user, page));

/** Every box that opens at least one page: holding any of them gets a person into the panel, to see only their pages. */
export const ADMIN_DOOR_KEYS = [...new Set(ADMIN_PAGES.flatMap((page) => [page.permission, page.alsoOpenedBy].filter(Boolean)))];

/** Whether the user may enter the Admin Panel at all: they can open at least one of its pages. */
export const canOpenAdmin = (user) => ADMIN_PAGES.some((page) => canOpenAdminPage(user, page));

/** The visible pages by rail group, skipping groups left empty by permissions. */
export const groupAdminPages = (pages) => ADMIN_GROUPS
  .map((group) => ({ ...group, pages: pages.filter((page) => page.group === group.id) }))
  .filter((group) => group.pages.length > 0);
